import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from '../hooks/useTranslation';
import { CheckCircle2, Loader2, AlertTriangle, ArrowRight, Lock, User, Sparkles } from 'lucide-react';
import { Button } from '../components/ui/button';
import axios from 'axios';

const PaymentSuccess = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { setUser } = useAuth();
  const { t } = useTranslation();
  const [phase, setPhase] = useState('verifying');
  const [paymentData, setPaymentData] = useState(null);
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    if (!sessionId) {
      setPhase('error');
      return;
    }

    const verifyPayment = async () => {
      try {
        const res = await axios.get(`/api/stripe/status/${sessionId}`);
        if (res.data.payment_status === 'paid') {
          setPaymentData(res.data);
          setPhase('create_account');
        } else {
          setTimeout(verifyPayment, 2000);
        }
      } catch (err) {
        console.error('Verification error:', err);
        setPhase('error');
      }
    };

    verifyPayment();
  }, [sessionId]);

  const handleCreateAccount = async () => {
    if (!name.trim() || password.length < 6) return;
    setSaving(true);
    setError('');

    try {
      const res = await axios.post('/api/stripe/register', {
        session_id: sessionId,
        name: name.trim(),
        password,
        language: 'fr',
      });

      localStorage.setItem('atlas_token', res.data.token);
      setUser(res.data);
      navigate('/onboarding');
    } catch (err) {
      console.error('Account creation error:', err);
      setError(err.response?.data?.detail || t('payment.error'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="payment-success-page" className="min-h-screen bg-atlasBg flex items-center justify-center px-6">
      <div className="absolute inset-0">
        <div className="absolute top-1/3 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] bg-atlasCyan/[0.04] rounded-full blur-[100px]" />
      </div>

      <div className="relative z-10 w-full max-w-md">
        {/* Verifying Payment */}
        {phase === 'verifying' && (
          <div className="text-center" data-testid="payment-verifying">
            <div className="relative mx-auto w-16 h-16 mb-6">
              <div className="absolute inset-0 rounded-full border-2 border-atlasCyan/20" />
              <div className="absolute inset-0 rounded-full border-2 border-atlasCyan border-t-transparent animate-spin" />
            </div>
            <p className="text-lg text-atlasCyan atlas-mono animate-pulse">{t('payment.verifying')}</p>
            <p className="text-atlasTextSecondary text-sm mt-3">{t('payment.verifyingDesc') || 'Confirmation en cours...'}</p>
          </div>
        )}

        {/* Create Account - Streamlined */}
        {phase === 'create_account' && (
          <div data-testid="payment-create-account">
            <div className="text-center mb-8">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-500/10 mb-5 ring-2 ring-green-500/20">
                <CheckCircle2 className="w-8 h-8 text-green-500" />
              </div>
              <p className="text-green-400 text-sm font-medium atlas-mono mb-3">{t('payment.success')}</p>
              <h1 className="text-2xl font-bold text-white mb-2">{t('payment.almostThere') || 'Plus qu\'une étape'}</h1>
              <p className="text-atlasTextSecondary text-sm">{t('payment.createAccountDesc')}</p>
              {paymentData?.customer_email && (
                <p className="text-atlasCyan text-sm mt-2 atlas-mono">{paymentData.customer_email}</p>
              )}
            </div>

            <div className="glass-card p-6 space-y-5">
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <User className="w-4 h-4 text-atlasTextSecondary" />
                  <label className="text-sm text-atlasTextSecondary">{t('payment.namePlaceholder') || 'Votre prénom'}</label>
                </div>
                <input
                  data-testid="payment-name-input"
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="w-full bg-atlasBg/50 border border-atlasBorder rounded-lg px-4 py-3 text-white outline-none focus:border-atlasCyan transition-colors"
                  placeholder={t('payment.namePlaceholder') || 'Prénom'}
                  autoFocus
                />
              </div>

              <div>
                <div className="flex items-center gap-2 mb-2">
                  <Lock className="w-4 h-4 text-atlasTextSecondary" />
                  <label className="text-sm text-atlasTextSecondary">{t('payment.passwordLabel') || 'Mot de passe'}</label>
                </div>
                <input
                  data-testid="payment-password-input"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full bg-atlasBg/50 border border-atlasBorder rounded-lg px-4 py-3 text-white outline-none focus:border-atlasCyan transition-colors"
                  placeholder="••••••••"
                />
                {password.length > 0 && password.length < 6 && (
                  <p className="text-atlasRed text-xs mt-1">{t('payment.passwordMin') || '6 caractères minimum'}</p>
                )}
              </div>

              {error && (
                <div className="flex items-center gap-2 text-atlasRed text-sm">
                  <AlertTriangle className="w-4 h-4 flex-shrink-0" />
                  {error}
                </div>
              )}

              <Button
                data-testid="payment-activate-btn"
                onClick={handleCreateAccount}
                disabled={saving || !name.trim() || password.length < 6}
                className="w-full btn-primary py-3.5 text-base flex items-center justify-center gap-2"
              >
                {saving ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <>
                    <Sparkles className="w-4 h-4" />
                    {t('payment.cta') || 'Activer mon accès ATLAS'}
                    <ArrowRight className="w-5 h-5" />
                  </>
                )}
              </Button>
            </div>

            <p className="text-center text-atlasTextSecondary/50 text-xs mt-5">
              {t('payment.emailConfirmation') || 'Un email de confirmation vous sera envoyé'}
            </p>
          </div>
        )}

        {/* Error */}
        {phase === 'error' && (
          <div className="text-center" data-testid="payment-error">
            <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-atlasRed/10 mb-4">
              <AlertTriangle className="w-7 h-7 text-atlasRed" />
            </div>
            <p className="text-atlasRed mb-4">{t('payment.error')}</p>
            <Button onClick={() => navigate('/')} className="btn-primary">
              {t('accessGuard.cta') || 'Retour'}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default PaymentSuccess;
